import React, { useEffect, useRef } from 'react';

import history from '@guestyci/history';

import { useOktaAuth } from '@okta/okta-react';
import * as OktaSignIn from '@okta/okta-signin-widget';
import { blue700 } from '@guestyci/foundation/theme/colors';
import { loginDio } from '../utils/eventsLogger';

import { setToken, setBiData } from '../utils/localStorage';
import { confirmMfa } from '../utils/api';
import getOktaTranslations from '../utils/loginTranslations';

import useDocumentTitle from '../utils/useDocumentTitle';
import usePageView from '../utils/usePageView';

import '@okta/okta-signin-widget/dist/css/okta-sign-in.min.css';
import config from '../okta-config';

import './Login.css';

const handleSuccess = ({ token, url }) => {
  setToken(token);
  setBiData(token);
  window.location.replace(url);
};

const translationKeyPrefix = 'auth_page:login.login';

const Login = () => {
  const { oktaAuth } = useOktaAuth();
  const {
    redirectUrl: wixRedirectUrl,
    return_to: zendeskRedirectUrl,
    ref: urlRef,
  } = history.getQueryParams();
  const isWixLogin = !!wixRedirectUrl;
  const isZendeskLogin = !!zendeskRedirectUrl;

  if (isWixLogin) {
    window.sessionStorage.setItem('wixRedirectURL', wixRedirectUrl);
  } else {
    window.sessionStorage.removeItem('wixRedirectURL');
  }

  if (isZendeskLogin) {
    window.sessionStorage.setItem('zendeskRedirectUrl', zendeskRedirectUrl);
  } else {
    window.sessionStorage.removeItem('zendeskRedirectUrl');
  }

  if (urlRef) {
    window.sessionStorage.setItem('ref', urlRef);
  } else {
    window.sessionStorage.removeItem('ref');
  }

  useDocumentTitle('Login');
  usePageView('login');

  useEffect(() => {
    const { s: mfaSecret } = history.getQueryParams();
    if (mfaSecret) {
      confirmMfa(mfaSecret)
        .then(response => {
          const token = response?.data?.token;
          if (token) {
            handleSuccess({ token, url: '/' });
          }
        })
        .catch(() => {});
    }
  }, []);

  const registerWidgetEvents = widget => {
    widget.on('afterError', (_context, error) => {
      console.log('After error called', { error });
    });

    widget.after('identify', async () => {
      console.log('Widget identify called');
    });

    widget.on('afterRender', context => {
      console.log('Rendered');
      console.log('After render called', { context });

      if (context?.controller !== 'primary-auth') {
        return;
      }

      addGuestyForHostsLink();
      const submitForm = document.getElementById('form1');
      submitForm.addEventListener('submit', () => {
        exitErrorState();
      });
    });
  };

  const processCreds = async (creds, callback) => {
    callback();
  };

  const widgetRef = useRef();

  useEffect(() => {
    if (!widgetRef.current) {
      return false;
    }

    const widgetConfig = getWidgetConfig(processCreds);
    const widget = new OktaSignIn(widgetConfig);
    registerWidgetEvents(widget);

    widget.showSignInAndRedirect({
      el: widgetRef.current,
    });

    return () => widget.remove();
  }, [oktaAuth]);

  return (
    <div>
      <div ref={widgetRef} />
    </div>
  );
};

export default Login;

function exitErrorState() {
  const errorContainer = document.getElementById('error-container');
  if (errorContainer) {
    errorContainer.remove();
  }
}

function addGuestyForHostsLink() {
  const container = document.getElementsByClassName('content-container')[0];

  const linkNode = document.createElement('a');
  linkNode.textContent = 'Guesty For Hosts Login';
  linkNode.href = 'https://app.guestyforhosts.com';
  linkNode.classList.add('gfh-login-link');
  linkNode.onclick = () => {
    loginDio.track('guesty_for_hosts_login', 'click', {});
  };

  container.prepend(linkNode);
}

function getUserLanguage() {
  const userLanguage = window.localStorage.getItem('lang');
  if (!userLanguage || userLanguage === 'en-US') {
    return 'en';
  }
  return userLanguage;
}

function getWidgetConfig(processCreds) {
  const {
    issuer,
    clientId,
    redirectUri,
    scopes,
    useInteractionCode,
  } = config.oidc;

  const userLanguage = getUserLanguage();
  const oktaLoginTranslations = getOktaTranslations(translationKeyPrefix);
  const searchParams = new URLSearchParams(window.location.search);
  const recoveryToken = searchParams.get('token');
  if (recoveryToken) {
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  return {
    baseUrl: issuer.split('/oauth2')[0],
    recoveryToken,
    clientId,
    redirectUri,
    logo: `${process.env.PUBLIC_URL}/guesty-logo.svg`,
    language: userLanguage,
    i18n: { [userLanguage]: oktaLoginTranslations },
    features: { rememberMe: false, registration: true },
    authParams: {
      issuer,
      scopes,
    },
    processCreds,
    useInteractionCodeFlow: useInteractionCode, // Set to true, if your org is OIE enabled
    brandName: 'Guesty',
    colors: {
      brand: blue700,
    },
    helpLinks: {
      forgotPassword: '/auth/forgot',
      help:
        'https://support.guesty.com/kb/en/article/troubleshooting-login-issues',
    },
    registration: {
      click() {
        window.location.href = 'https://www.guesty.com/request-a-demo/';
      },
    },
  };
}
